/**
 * @file Popover de compte pour le header mobile admin.
 * @module layouts.admin.admin-account-popover
 *
 * Version du popover de compte adaptee au panel admin :
 * recoit l'instance popover en prop (contrairement au AccountPopover
 * qui gere son propre etat interne).
 */

import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
// hooks
import {useAuthContext} from 'packages/common/hooks';
import {useSession} from 'packages/common/hooks/session';
import {useRouter} from 'packages/common/routes/hooks';
// components
import CustomPopover from 'packages/common/components/custom-popover';
// routes
import {paths} from 'packages/common/routes/paths';

/**
 * Popover de compte admin, pilote par une instance de usePopover externe.
 *
 * @param {Object} props
 * @param {Object} props.popover - Instance usePopover (open, onClose, anchorEl)
 * @return {JSX.Element}
 */
export default function AdminAccountPopover({popover}) {
  const router = useRouter();
  const {logout} = useAuthContext();
  const {user} = useSession();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
    } catch (error) {
      console.error('Erreur lors de la deconnexion :', error);
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <CustomPopover
      hiddenArrow
      open={popover.open}
      onClose={popover.onClose}
      sx={{
        width: 280,
        p: 0,
        bgcolor: (theme) => theme.palette.navy[900],
        backgroundImage: 'none',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(15, 28, 46, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      transformOrigin={{vertical: 'top', horizontal: 'right'}}
    >
      <Box sx={{p: 2.5}}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            src={user?.photoURL}
            alt={`${user?.firstName} ${user?.lastName}`}
            sx={{
              width: 48,
              height: 48,
              bgcolor: (theme) => theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
            }}
          >
            {user?.firstName?.charAt(0)?.toUpperCase() || ''}
          </Avatar>
          <Stack sx={{minWidth: 0, flex: 1}}>
            <Typography variant="subtitle2" noWrap sx={{color: '#E2E8F0'}}>
              {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
            </Typography>
            <Typography
              variant="body2"
              sx={{color: 'rgba(226, 232, 240, 0.7)', fontSize: '0.8rem'}}
              noWrap
            >
              {user?.email}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{borderColor: 'rgba(255, 255, 255, 0.12)'}} />

      <Box sx={{p: 1}}>
        <MenuItem
          onClick={() => handleClickItem(paths.admin.root)}
          sx={{
            'px': 2,
            'py': 1.5,
            'borderRadius': 1,
            'color': '#E2E8F0',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <Typography variant="body2">Accueil</Typography>
        </MenuItem>

        <Divider sx={{my: 1, borderColor: 'rgba(255, 255, 255, 0.12)'}} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            'px': 2,
            'py': 1.5,
            'gap': 1.5,
            'borderRadius': 1,
            'color': 'error.main',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <ListItemIcon sx={{minWidth: 'auto'}}>
            <LogoutRoundedIcon sx={{fontSize: 20, color: 'error.main'}} />
          </ListItemIcon>
          <Typography variant="body2">Deconnexion</Typography>
        </MenuItem>
      </Box>
    </CustomPopover>
  );
}

AdminAccountPopover.propTypes = {
  popover: PropTypes.object.isRequired,
};
